import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ConvertLeadInput, LeadResponse } from '@orion/contracts';
import { Button, Select } from '@orion/ui';
import { useCompaniesQuery } from '../companies/api.js';
import { useContactsQuery } from '../contacts/api.js';
import { usePipelinesQuery } from '../deals/api.js';
import { useConvertLead } from './api.js';

/** design/orion-crm-design/rendered-html/LeadConvert.html — a modal over
 * Lead detail, not a separate page. Company/contact match detection is
 * real (GET /companies|contacts?q=), not simulated: it searches by the
 * lead's actual companyName/email. Deal owner isn't in the modal because
 * it isn't in convertLeadSchema either — ConvertLeadService always
 * defaults it to the lead's own owner, so a picker here would be decorative
 * and could silently disagree with what actually happens. */
export function ConvertLeadModal({ lead, onClose }: { lead: LeadResponse; onClose: () => void }) {
  const navigate = useNavigate();
  const convertLead = useConvertLead(lead.id);
  const { data: companyMatches } = useCompaniesQuery({ q: lead.companyName ?? undefined, pageSize: 3 });
  const { data: contactMatches } = useContactsQuery({ q: lead.email ?? lead.fullName, pageSize: 3 });
  const { data: pipelines } = usePipelinesQuery();

  const companyMatch = companyMatches?.data[0];
  const contactMatch = contactMatches?.data[0];

  const [companyMode, setCompanyMode] = useState<'link' | 'create' | 'none'>('create');
  const [contactMode, setContactMode] = useState<'link' | 'create'>('create');
  const [createDeal, setCreateDeal] = useState(true);
  const [dealName, setDealName] = useState(`${lead.companyName ?? lead.fullName} — New business`);
  const [amount, setAmount] = useState('0');
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [source, setSource] = useState(lead.source?.name ?? '');

  useEffect(() => {
    if (companyMatch) setCompanyMode('link');
    else setCompanyMode(lead.companyName ? 'create' : 'none');
  }, [companyMatch, lead.companyName]);

  useEffect(() => {
    if (pipelines && pipelines.length > 0 && !pipelineId) {
      const def = pipelines.find((p) => p.isDefault) ?? pipelines[0]!;
      setPipelineId(def.id);
      const openStage = def.stages.find((s) => s.type === 'open');
      if (openStage) setStageId(openStage.id);
    }
  }, [pipelines, pipelineId]);

  const selectedPipeline = pipelines?.find((p) => p.id === pipelineId);
  const selectedStage = selectedPipeline?.stages.find((s) => s.id === stageId);

  const submit = async () => {
    const input: ConvertLeadInput = {
      company:
        companyMode === 'link' && companyMatch
          ? { mode: 'link', companyId: companyMatch.id }
          : companyMode === 'create'
            ? { mode: 'create' }
            : undefined,
      contact: contactMode === 'link' && contactMatch ? { mode: 'link', contactId: contactMatch.id } : { mode: 'create' },
      deal: createDeal
        ? { name: dealName, pipelineId, stageId, amount: Number(amount) || 0, currency: 'USD', expectedCloseDate: expectedCloseDate || undefined, source: source.trim() || undefined }
        : undefined,
    };
    const result = await convertLead.mutateAsync(input);
    onClose();
    await navigate({ to: '/leads/$leadId', params: { leadId: result.leadId } });
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-night/42" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="cv-title" className="fixed left-1/2 top-13 z-50 flex w-180 -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-dialog">
        <header className="flex items-center gap-3.5 border-b border-border px-6 py-5">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-input bg-primary-soft">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2E47D6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 12a8 8 0 1 1-2.3-5.7L20 8" />
              <path d="M20 3v5h-5" />
            </svg>
          </span>
          <div className="flex flex-1 flex-col gap-0.5">
            <h2 id="cv-title" className="text-[17px] font-[650] leading-5.5 text-ink">
              Convert {lead.fullName}
            </h2>
            <span className="text-[13px] text-text-muted">
              {lead.status.name} lead{lead.companyName ? ` · ${lead.companyName}` : ''}
              {lead.owner ? ` · owned by ${lead.owner.displayName}` : ''}
            </span>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="flex size-8 items-center justify-center rounded-control text-text-secondary hover:bg-surface-subtle">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </header>

        <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto px-6 py-5">
          {lead.companyName && (
            <fieldset className="flex flex-col gap-2.5 rounded-xl border border-border p-4">
              <legend className="sr-only">Company</legend>
              <StepHeader n={1} label="Company" done={companyMode === 'link'} doneLabel="Match found" />
              {companyMatch && (
                <p className="text-[13px] leading-4.5 text-text-secondary">
                  Match found: <strong className="font-semibold text-ink">{companyMatch.name}</strong> (existing company)
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                {companyMatch && (
                  <ChoiceCard checked={companyMode === 'link'} onSelect={() => setCompanyMode('link')} title="Link to existing" subtitle={companyMatch.name} />
                )}
                <ChoiceCard checked={companyMode === 'create'} onSelect={() => setCompanyMode('create')} title="Create new company" subtitle={`Adds "${lead.companyName}"`} />
              </div>
            </fieldset>
          )}

          <fieldset className="flex flex-col gap-2.5 rounded-xl border border-border p-4">
            <legend className="sr-only">Contact</legend>
            <StepHeader n={2} label="Contact" done={!contactMatch} doneLabel="No duplicate contacts" />
            <div className="grid grid-cols-2 gap-3">
              <ChoiceCard checked={contactMode === 'create'} onSelect={() => setContactMode('create')} title={`Create new contact ${lead.fullName}`} subtitle={lead.jobTitle ?? ''} />
              {contactMatch && (
                <ChoiceCard checked={contactMode === 'link'} onSelect={() => setContactMode('link')} title="Link to existing contact" subtitle={contactMatch.fullName} />
              )}
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-3 rounded-xl border border-border p-4">
            <legend className="sr-only">Opportunity</legend>
            <div className="flex items-center gap-2.5">
              <span className="flex size-5.5 shrink-0 items-center justify-center rounded-full bg-ink text-xs font-[650] text-white">3</span>
              <span className="flex-1 text-sm font-semibold text-ink">Opportunity</span>
              <label className="flex items-center gap-2 text-[13px] font-semibold text-ink">
                <input type="checkbox" checked={createDeal} onChange={(e) => setCreateDeal(e.target.checked)} className="size-4 accent-primary" />
                Create an opportunity
              </label>
            </div>
            {createDeal && (
              <div className="grid grid-cols-3 gap-3">
                <label className="col-span-2 flex flex-col gap-1 text-xs font-semibold text-text-secondary">
                  Opportunity name
                  <input value={dealName} onChange={(e) => setDealName(e.target.value)} className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm text-ink" />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold text-text-secondary">
                  Value
                  <div className="flex h-9 items-center rounded-control border border-border-strong bg-surface">
                    <span className="pl-3 pr-1 text-sm text-text-muted">$</span>
                    <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="w-full bg-transparent pr-2 text-sm text-ink focus:outline-none" />
                  </div>
                </label>
                <Select
                  label="Pipeline"
                  hideLabel={false}
                  value={pipelineId}
                  onChange={(e) => {
                    setPipelineId(e.target.value);
                    const p = pipelines?.find((x) => x.id === e.target.value);
                    const openStage = p?.stages.find((s) => s.type === 'open');
                    if (openStage) setStageId(openStage.id);
                  }}
                  options={(pipelines ?? []).map((p) => ({ value: p.id, label: p.name }))}
                />
                <Select
                  label="Stage"
                  hideLabel={false}
                  value={stageId}
                  onChange={(e) => setStageId(e.target.value)}
                  options={(selectedPipeline?.stages ?? []).map((s) => ({ value: s.id, label: s.name }))}
                />
                <label className="flex flex-col gap-1 text-xs font-semibold text-text-secondary">
                  Expected close
                  <input type="date" value={expectedCloseDate} onChange={(e) => setExpectedCloseDate(e.target.value)} className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm text-ink" />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold text-text-secondary">
                  Source
                  <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. Referral" className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm text-ink" />
                </label>
                <span className="col-span-2 flex items-end pb-2 text-xs text-text-muted">
                  Linked to {lead.companyName ?? lead.fullName}{selectedStage ? ` · ${selectedStage.probability}% win probability for ${selectedStage.name}` : ''}
                </span>
              </div>
            )}
          </fieldset>

          <div role="note" className="flex gap-2.5 rounded-input bg-primary-soft p-3">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2439B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5M12 8h.01" />
            </svg>
            <span className="text-[13px] leading-4.75 text-primary-text">
              The full lead history — activities, notes, tasks and attachments — moves with the record. The original lead is kept as <strong className="font-semibold">Converted</strong>.
            </span>
          </div>
        </div>

        <footer className="flex items-center gap-2 border-t border-border bg-ground px-6 py-3.5">
          <span className="flex-1 text-xs text-text-muted">
            Creates {contactMode === 'create' ? 1 : 0} contact{createDeal ? ' and 1 opportunity' : ''}
            {companyMode === 'create' ? ' and 1 company' : companyMode === 'link' ? ' · links 1 company' : ''}
          </span>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} loading={convertLead.isPending}>
            Convert lead
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Button>
        </footer>
      </div>
    </>
  );
}

function StepHeader({ n, label, done, doneLabel }: { n: number; label: string; done: boolean; doneLabel: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex size-5.5 shrink-0 items-center justify-center rounded-full bg-ink text-xs font-[650] text-white">{n}</span>
      <span className="flex-1 text-sm font-semibold text-ink">{label}</span>
      {done && (
        <span className="flex items-center gap-1 text-xs font-semibold text-success">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m5 12 5 5 9-10" />
          </svg>
          {doneLabel}
        </span>
      )}
    </div>
  );
}

function ChoiceCard({ checked, onSelect, title, subtitle }: { checked: boolean; onSelect: () => void; title: string; subtitle: string }) {
  return (
    <label className={`flex cursor-pointer items-start gap-2.5 rounded-input border p-2.5 ${checked ? 'border-primary bg-primary-tint shadow-focus-ring' : 'border-border bg-surface'}`}>
      <input type="radio" checked={checked} onChange={onSelect} className="mt-0.5 size-4 accent-primary" />
      <span className="flex flex-col gap-0.5">
        <span className="text-[13px] font-semibold text-ink">{title}</span>
        <span className="text-xs text-text-muted">{subtitle}</span>
      </span>
    </label>
  );
}
